import { useState, useEffect, useRef, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useSearch } from "wouter";
import { Loader2, Package, Printer, Download, ArrowLeft, CheckCircle2, X, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { isNativeApp } from "@/lib/native/platform";
import { hasNativePrinter, hasNativeShare, printPdfNatively, sharePdfNatively } from "@/lib/native/printer";
import { apiRequest, getApiPath, readPath } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { OrganizationContext } from "@/contexts/OrganizationContext";
import { ChangeOrderButton } from "@/components/change-order-button";
import { signInHref, type CustomerSessionInfo } from "@/lib/orderSummaryLinks";
import freshorderLogo from "../assets/freshorder-logo.png";
import type { OrderWithDetails, Organization, Category } from "@shared/schema";
import { categoryRanks, sortOrderLines } from "@shared/orderLineSort";
import { formatDeliveryFeePrice, type DeliveryFeeLine } from "@shared/deliveryFee";

export default function OrderSummary() {
  const { orgSlug, accountId, orderId } = useParams<{ orgSlug: string; accountId: string; orderId: string }>();

  // Detect whether we can go back (handles iOS standalone PWA with no history)
  const canGoBack = window.history.length > 1;

  // Show a green success banner when navigated here immediately after order submission
  const search = useSearch();
  const searchParams = new URLSearchParams(search);
  const justSubmitted = searchParams.get("submitted") === "1";
  // Signed access token from a texted receipt link — lets this page load the order
  // without a login cookie (the SMS often opens in a browser that doesn't have one).
  const accessToken = searchParams.get("t");
  // Set by the native shell's Print hop: this page was opened in the SYSTEM browser
  // precisely so a print dialog can be raised, so raise it once the order has rendered.
  const autoPrint = searchParams.get("print") === "1";
  // Stays until the buyer closes it (×) or leaves — a 4 s timer took it away before
  // a first-time buyer had read it (Jon, 2026-10-25).
  const [showSuccessBanner, setShowSuccessBanner] = useState(justSubmitted);

  const handleBack = () => {
    if (canGoBack) {
      window.history.back();
    } else {
      const pathParts = window.location.pathname.split('/');
      const orgIndex = pathParts.indexOf('org');
      if (orgIndex !== -1 && pathParts[orgIndex + 1]) {
        window.location.href = `/org/${pathParts[orgIndex + 1]}`;
      } else {
        window.location.href = '/';
      }
    }
  };

  const orgBrand = useContext(OrganizationContext)?.organization;
  const { data: organization, isLoading: orgLoading } = useQuery<Organization>({
    queryKey: ["/api/organizations/slug", orgSlug],
    enabled: Boolean(orgSlug),
  });

  // `editable`: the ordering page will open this order for changes (the server's rule).
  // `deliveryFee`: the vendor's delivery charge on a delivery order, or null — not one of
  // the order's lines (server/services/deliveryFee.ts), so it counts in no total here.
  const { data: order, isLoading: orderLoading, error: orderError } = useQuery<OrderWithDetails & { editable?: boolean; deliveryFee?: DeliveryFeeLine | null }>({
    // The key names the org like every other read here; the path is fetched as-is
    // (getApiPath scopes it) so the default query function's `${status}: ${body}`
    // error, which errorStatus below reads, is unchanged.
    queryKey: ["/api/customer/order", orderId, accessToken, orgSlug],
    queryFn: readPath<OrderWithDetails & { editable?: boolean; deliveryFee?: DeliveryFeeLine | null }>(
      `/api/customer/order/${orderId}${accessToken ? `?t=${encodeURIComponent(accessToken)}` : ""}`,
    ),
    enabled: Boolean(orderId) && Boolean(orgSlug),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // The default queryFn throws `${status}: ${body}` on a failed fetch, so the
  // leading HTTP status is recoverable from the error message. A 401/403 means
  // "you're not signed in here" (common when the SMS link opens in a browser
  // without the session cookie) — that must NOT masquerade as a missing order.
  const errorStatus = orderError
    ? Number(String((orderError as Error).message).match(/^(\d{3})/)?.[1])
    : undefined;
  const isAuthError = errorStatus === 401 || errorStatus === 403;

  // Is this browser signed in, and to which account? Decides where "Change this
  // order" goes: the ordering page, or the sign-in card first. Asked only while the
  // order can still be changed. No session (401) is an answer, not an error.
  const { data: session, isLoading: sessionLoading } = useQuery<CustomerSessionInfo | null>({
    queryKey: [getApiPath("/api/customer/verify-session"), "order-summary"],
    queryFn: async () => {
      try {
        const data = await (await apiRequest("POST", "/api/customer/verify-session", {})).json();
        return data?.valid ? { accountId: data.accountId ?? null, customUrl: data.customUrl ?? null } : null;
      } catch {
        return null;
      }
    },
    enabled: Boolean(order?.editable),
    retry: false,
    staleTime: 0,
  });

  // The key names the org. The default query function scopes a bare "/api/..." key the
  // same way, but an org-blind key is one cache entry for every vendor this buyer visits
  // (the #260 collision class), so the key carries the slug like every other read.
  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: [getApiPath("/api/categories")],
  });

  const isLoading = orgLoading || orderLoading;

  const { toast } = useToast();

  // ---- Print / Download -------------------------------------------------------------
  // iOS WKWebView does not implement window.print(): it is a silent no-op, which is why
  // this button appeared dead in the native shell (TestFlight, 2026-09-04). The shell
  // hands the page to the SYSTEM browser instead: Capacitor's iOS delegate turns
  // window.open(url, "_blank") into UIApplication.open(). Safari has its own cookie jar,
  // so the URLs we hand it are SIGNED (same token shape as the SMS receipts).
  //
  // The signed URLs are PREFETCHED, not fetched on tap. WebKit only honours
  // window.open while the tap's user activation is still live, and an `await fetch`
  // before the open can outlast it on a real network — that is exactly why the first
  // version worked in the simulator and did nothing on a phone. With the URLs already
  // in state, the open happens synchronously inside the click handler.
  const native = isNativeApp();
  const [signed, setSigned] = useState<{ printUrl: string; pdfUrl: string } | null>(null);
  useEffect(() => {
    if (!native || !orderId || accessToken) return; // in Safari (token) we are not native
    let cancelled = false;
    fetch(getApiPath(`/api/customer/order/${orderId}/print-url`), { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled && j?.printUrl && j?.pdfUrl) setSigned({ printUrl: j.printUrl, pdfUrl: j.pdfUrl }); })
      .catch(() => { /* the tap falls back to a toast below */ });
    return () => { cancelled = true; };
  }, [native, orderId, accessToken]);

  const notReady = () =>
    toast({
      title: "One moment",
      description: "Still preparing this order — please try again.",
      variant: "destructive",
    });

  const [busy, setBusy] = useState<"print" | "download" | null>(null);
  const inAppPdfUrl = getApiPath(`/api/customer/order/${orderId}/pdf`); // session cookie works in-app
  const fileName = `order-ORD-${String(orderId).padStart(3, "0")}.pdf`;
  const failed = (what: string) =>
    toast({ title: `Couldn't ${what}`, description: "Please try again.", variant: "destructive" });

  const handlePrint = async () => {
    if (!native) { window.print(); return; }
    // Builds with the in-repo Printer plugin: iOS print sheet, in-app, one tap.
    if (hasNativePrinter()) {
      setBusy("print");
      try { await printPdfNatively(inAppPdfUrl, fileName.replace(".pdf", "")); }
      catch { failed("print"); }
      finally { setBusy(null); }
      return;
    }
    // Older builds: hand the signed page to Safari (synchronous — see above).
    if (!signed) return notReady();
    window.open(signed.printUrl, "_blank");
  };

  // Web: the PDF route honours the session cookie, or the ?t= token this page was opened
  // with (Safari after the native hop). Native: the signed pdfUrl, synchronously.
  const webPdfUrl = `${getApiPath(`/api/customer/order/${orderId}/pdf`)}?download=1${accessToken ? `&t=${encodeURIComponent(accessToken)}` : ""}`;
  const handleDownload = async () => {
    if (native) {
      // Builds with Share + Filesystem: the iOS share sheet — Save to Files (iCloud
      // Drive, Google Drive, On My iPhone), AirDrop, Mail, Print.
      if (hasNativeShare()) {
        setBusy("download");
        try { await sharePdfNatively(inAppPdfUrl, fileName, `Order ${fileName.replace(".pdf", "")}`); }
        catch { failed("save"); }
        finally { setBusy(null); }
        return;
      }
      if (!signed) return notReady();
      window.open(signed.pdfUrl, "_blank");
      return;
    }
    const a = document.createElement("a");
    a.href = webPdfUrl;
    a.download = "";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Raise the print dialog once, after the order has actually rendered. Guarded by a
  // ref so a refetch or a re-render never re-opens it. rAF lets the layout settle first.
  const autoPrintFired = useRef(false);
  useEffect(() => {
    if (!autoPrint || !order || autoPrintFired.current) return;
    autoPrintFired.current = true;
    const raf = requestAnimationFrame(() => window.print());
    return () => cancelAnimationFrame(raf);
  }, [autoPrint, order]);

  // The lines in the vendor's own order — the one the pick sheet and the invoice use
  // (shared/orderLineSort.ts).
  const sortedItems = order?.items ? sortOrderLines(order.items, categoryRanks(categories)) : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading order...</p>
        </div>
      </div>
    );
  }

  // An auth failure (no valid session cookie AND no valid token) is NOT a missing
  // order — most often the SMS link was opened in a browser/app where the customer
  // isn't signed in. Guide them to sign in instead of falsely saying "removed".
  if (isAuthError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Card className="max-w-md mx-4">
          <CardHeader>
            <CardTitle className="text-primary flex items-center gap-2">
              <LogIn className="h-5 w-5" />
              Sign in to view your order
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Your order is safe — you just need to be signed in on this device to view it.
              Sign in and you'll come straight back to it.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                // Back to this summary once signed in (never with the ?t= token: the
                // session cookie opens it then).
                onClick={() => {
                  window.location.href = orgSlug
                    ? signInHref(orgSlug, { returnTo: `/org/${orgSlug}/order-summary/${accountId}/${orderId}` })
                    : '/';
                }}
                className="flex items-center gap-2"
                data-testid="button-sign-in"
              >
                <LogIn className="h-4 w-4" />
                Sign In
              </Button>
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (orderError || !order) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Card className="max-w-md mx-4">
          <CardHeader>
            <CardTitle className="text-red-600 flex items-center gap-2">
              <Package className="h-5 w-5" />
              Order Not Found
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              We couldn't find the order you're looking for. It may have been removed or the link may be incorrect.
            </p>
            <Button
              variant="outline"
              onClick={handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const orderDate = order.createdAt 
    ? new Date(order.createdAt).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
      })
    : '';

  const orderNumber = order.id?.toString().padStart(3, '0') || '000';

  return (
    <div
      className="min-h-screen bg-gray-50 print:bg-white"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className="max-w-2xl mx-auto p-4 print:p-2">
        <div className="print:hidden mb-4 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Button
              onClick={handlePrint}
              disabled={busy !== null}
              className="flex items-center gap-2"
              data-testid="button-print-order-summary"
            >
              {busy === "print" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
              Print
            </Button>
            <Button
              onClick={handleDownload}
              disabled={busy !== null}
              className="flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700"
              data-testid="button-download-order-summary"
            >
              {busy === "download" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download
            </Button>
          </div>
        </div>

        {/* The vendor's brand on the customer's copy of the order (screen and print). */}
        {(orgBrand?.name || organization?.name) && (
          <div className="mb-4 flex items-center gap-3" data-testid="order-summary-org">
            <img
              src={orgBrand?.logoUrl || freshorderLogo}
              alt={`${orgBrand?.name || organization?.name} logo`}
              className="h-10 w-10 object-contain"
              data-testid="img-org-logo"
            />
            <span className="text-lg font-semibold text-gray-900">{orgBrand?.name || organization?.name}</span>
          </div>
        )}
        {showSuccessBanner && (
          <div
            role="status"
            aria-live="polite"
            className="mb-4 flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-800 shadow-sm"
            data-testid="order-submitted-banner"
          >
            <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600" />
            <div className="flex-1">
              <div className="font-semibold">Order submitted successfully</div>
              <div className="text-sm text-green-700">
                Your order is in. Print or download it above, or go Back to revise.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowSuccessBanner(false)}
              className="ml-2 rounded p-1 text-green-700 hover:bg-green-100"
              aria-label="Dismiss notification"
              data-testid="dismiss-success-banner"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {orgSlug && order.editable && (
          <div className="print:hidden mb-4">
            <ChangeOrderButton
              orgSlug={orgSlug}
              orderId={order.id}
              orderAccountId={order.accountId}
              editable={Boolean(order.editable)}
              session={session ?? null}
              sessionPending={sessionLoading}
            />
          </div>
        )}

        <Card className="print:shadow-none print:border-0">
          <CardHeader className="text-center border-b print:border-b-2 print:border-black">
            <CardTitle className="text-lg font-bold">
              {order.account?.name || 'Customer'} - {orderDate} - #ORD-{orderNumber}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {sortedItems.length > 0 ? (
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100 print:bg-gray-200">
                    <th className="border border-gray-300 print:border-black p-2 text-center w-20">Qty</th>
                    <th className="border border-gray-300 print:border-black p-2 text-left">Item</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="border border-gray-300 print:border-black p-2 text-center font-medium">
                        {item.quantity}
                      </td>
                      <td className="border border-gray-300 print:border-black p-2">
                        {item.product?.name || 'Unknown Product'}
                      </td>
                    </tr>
                  ))}
                  {order.deliveryFee && (
                    <tr data-testid="order-summary-delivery-fee">
                      <td className="border border-gray-300 print:border-black p-2 text-center font-medium">
                        {order.deliveryFee.quantity}
                      </td>
                      <td className="border border-gray-300 print:border-black p-2">
                        <span className="flex justify-between gap-2">
                          <span>{order.deliveryFee.name}</span>
                          <span className="tabular-nums">{formatDeliveryFeePrice(order.deliveryFee.price)}</span>
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <p className="p-4 text-center text-gray-500">No items in this order.</p>
            )}

            {order.notes && (
              <div className="p-4 border-t">
                <p className="font-bold mb-2">Notes:</p>
                <p className="text-gray-700 whitespace-pre-line">{order.notes}</p>
              </div>
            )}

            <div className="p-4 border-t bg-gray-50 print:bg-white">
              <p className="text-center font-medium">
                Total Items: {order.totalItems}
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="print:hidden mt-4 text-center text-sm text-gray-500">
          {organization?.name && (
            <p>Thank you for ordering from {organization.name}</p>
          )}
        </div>
      </div>

      <style>{`
        @media print {
          body { 
            -webkit-print-color-adjust: exact; 
            print-color-adjust: exact;
          }
          .print\\:hidden { display: none !important; }
          .print\\:bg-white { background-color: white !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:border-0 { border: none !important; }
          .print\\:border-b-2 { border-bottom-width: 2px !important; }
          .print\\:border-black { border-color: black !important; }
          .print\\:bg-gray-200 { background-color: #e5e7eb !important; }
          .print\\:p-2 { padding: 0.5rem !important; }
        }
      `}</style>
    </div>
  );
}
